import { 
  IndianRupee, 
  TrendingUp, 
  FileText, 
  Shield, 
  Zap,
  Sparkles,
  Users,
  Camera,
  AlertTriangle,
  Wifi,
} from "lucide-react";

const features = [
  {
    icon: Camera,
    title: "AI Receipt Scan",
    description: "Snap a photo. Numbers fill themselves. No typing. No mistakes.",
    gradient: "from-primary to-accent",
    size: "large",
  },
  {
    icon: AlertTriangle,
    title: "Cash Mismatch Alerts",
    description: "₹50 missing? Know immediately.",
    gradient: "from-destructive/80 to-destructive",
    size: "small",
  },
  {
    icon: TrendingUp,
    title: "Profit Compass",
    description: "Daily, weekly, monthly. Always know north.",
    gradient: "from-success to-primary",
    size: "small",
  },
  {
    icon: Sparkles,
    title: "AI Copilot",
    description: '"What was my profit last Tuesday?" Your AI assistant knows everything about your business.',
    gradient: "from-accent to-primary",
    size: "large",
  },
  {
    icon: Users,
    title: "Team Roster",
    description: "Staff can enter. You control what they see.",
    gradient: "from-primary-dark to-primary",
    size: "small",
  },
  {
    icon: FileText,
    title: "Loan Documents",
    description: "Bank asks for records? One click.",
    gradient: "from-accent-glow to-accent",
    size: "small",
  },
  {
    icon: Shield,
    title: "Bank-Grade Security",
    description: "Your data is encrypted and secure. Your business stays private.",
    gradient: "from-primary to-primary-dark",
    size: "small",
  },
  {
    icon: Wifi,
    title: "Works Offline",
    description: "Bad network? No problem.",
    gradient: "from-success to-success",
    size: "small",
  },
];

export const FeaturesSection = () => {
  return (
    <section id="features" className="relative py-20 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/30 to-background" />
      <div className="absolute top-1/4 left-0 w-1/2 h-96 bg-primary/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 right-0 w-1/2 h-96 bg-accent/5 rounded-full blur-[120px]" />

      <div className="container relative z-10 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <div className="text-center space-y-4 mb-12 md:mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Built for Your Business</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-balance px-2">
              Not Just an App.
              <br className="hidden sm:block" />
              <span className="text-gradient">A Business Partner.</span>
            </h2>
            <p className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
              Every feature designed to give you clarity and save you time
            </p>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`group relative p-6 md:p-8 rounded-2xl md:rounded-3xl bg-card border border-border hover:border-primary/50 transition-all duration-500 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1
                  ${feature.size === 'large' ? 'sm:col-span-2' : ''}`}
              >
                {/* Hover gradient */}
                <div className={`absolute inset-0 rounded-2xl md:rounded-3xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`} />
                
                {/* Icon */}
                <div className={`relative w-12 h-12 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 md:mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="h-6 w-6 md:h-7 md:w-7 text-primary-foreground" />
                </div>

                {/* Content */}
                <h3 className="font-display text-lg md:text-xl font-semibold mb-2 md:mb-3 group-hover:text-primary transition-colors">
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
